import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ticket, Plus, Trash2 } from 'lucide-react';
import type { CouponAdmin, CouponUpsertBody } from '@evi/shared';
import { EVI_COUPON_MAX_PERCENT, EVI_COUPON_PERCENT_MESSAGE, formatLosAngelesDate } from '@evi/shared';
import {
  fetchCouponsAdmin, createCouponAdmin, updateCouponAdmin, toggleCouponAdmin,
  deleteCouponAdmin, getDisplayErrorMessage,
} from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { Modal } from '@/components/ui/Modal';
import { couponDateInput, couponDatePayload } from '@/lib/couponDates';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { usePromotionAccess } from '@/hooks/usePromotionAccess';
import styles from './CouponsPage.module.css';
import { Switch } from '@/components/ui/Switch';

export function CouponsPage() {
  useRecentRoute('Coupons');
  const access=usePromotionAccess('coupons');
  const qc = useQueryClient();
  const { data: coupons = [], isLoading, error } = useQuery({
    queryKey: ['coupons', 'admin'],
    queryFn: fetchCouponsAdmin,
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState<CouponAdmin | null>(null);
  const [creating, setCreating] = useState(false);

  // A FAILED WRITE HERE USED TO BE INVISIBLE.
  //
  // Neither mutation had an onError and main.tsx installs no
  // MutationCache handler, so a rejected request did nothing at all:
  // no toast, no console line, no row change. Delete 403s for an
  // operator (coupon:delete is not in their matrix — they hold
  // coupon:read/write only), so pressing Delete looked like a UI that
  // had simply stopped responding.
  const [actionError, setActionError] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: toggleCouponAdmin,
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['coupons', 'admin'] });
    },
    onError: async (e: Error) => setActionError(
      await getDisplayErrorMessage(e, 'Could not update that coupon.'),
    ),
  });

  const remove = useMutation({
    mutationFn: deleteCouponAdmin,
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['coupons', 'admin'] });
    },
    onError: async (e: Error) => setActionError(
      await getDisplayErrorMessage(e, 'Could not delete that coupon.'),
    ),
  });

  const columns: Column<CouponAdmin>[] = [
    {
      key: 'code',
      header: 'Code',
      mobile: 'primary',
      cell: (c) => <code className={styles.codePill}>{c.code}</code>,
    },
    {
      key: 'name',
      header: 'Name',
      mobile: 'secondary',
      cell: (c) => c.name,
    },
    {
      key: 'discount',
      header: 'Discount',
      mobile: 'body',
      cell: (c) => formatDiscount(c),
    },
    {
      key: 'scope',
      header: 'Applies to',
      mobile: 'body',
      cell: (c) => formatScope(c.appliesTo),
    },
    {
      key: 'claimed',
      header: 'Claimed',
      align: 'right',
      mobile: 'body',
      cell: (c) =>
        `${c.totalClaimed}${c.maxTotalClaims ? ` / ${c.maxTotalClaims}` : ''}`,
    },
    {
      key: 'expires',
      header: 'Expires',
      mobile: 'body',
      cell: (c) => (c.validUntil ? formatDate(c.validUntil) : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (c) => (
        <button
          type="button"
          className={`${styles.statusToggle} ${c.isActive ? styles.statusActive : styles.statusDisabled}`}
          disabled={!access.update}
          onClick={(e) => {
            e.stopPropagation();
            toggle.mutate(c.id);
          }}
        >
          {c.isActive ? 'Active' : 'Disabled'}
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      mobile: 'body',
      cell: (c) => access.delete ? (
        <button
          type="button"
          title="Delete coupon"
          aria-label={`Delete ${c.code}`}
          disabled={remove.isPending}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--danger, #e5484d)', padding: 4, lineHeight: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            const claimedNote = c.totalClaimed > 0
              ? ` ${c.totalClaimed} driver(s) already claimed it; they keep their claim, but it leaves the catalog.`
              : '';
            if (window.confirm(`Delete coupon "${c.code}"? This can't be undone.${claimedNote}`)) {
              remove.mutate(c.id);
            }
          }}
        >
          <Trash2 size={15} />
        </button>
      ) : null,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Ticket size={18} /></div>
          <div>
            <h1 className={styles.title}>Coupons</h1>
            <p className={styles.subtitle}>
              {coupons.length} configured{coupons.length === 1 ? '' : 's'.slice(0, 0)}
            </p>
          </div>
        </div>
        {access.create && <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setCreating(true)}
        >
          <Plus size={16} /> New Coupon
        </button>}
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error instanceof Error ? error.message : 'Failed to load coupons'}
        </div>
      )}

      {actionError && (
        <div className={styles.errorBanner}>{actionError}</div>
      )}

      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={coupons}
          rowKey={(c) => c.id}
          onRowClick={access.update?(c) => setEditing(c):undefined}
          emptyMessage={
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>No coupons yet</p>
              <p className={styles.emptyDesc}>
                {access.create?'Create your first coupon to give drivers discounts on charging or top-ups.':'Coupons are managed by EV Initiative.'}
              </p>
              {access.create && <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => setCreating(true)}
              >
                <Plus size={16} /> Create your first coupon
              </button>}
            </div>
          }
        />
      )}

      {(creating || editing) && (
        <CouponFormSheet
          coupon={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['coupons', 'admin'] });
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

// ── Create / edit form ───────────────────────────────────────────────

function CouponFormSheet({
  coupon, onClose, onSaved,
}: { coupon: CouponAdmin | null; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();

  // `code` + `discountType` are locked once a coupon exists — changing
  // either would invalidate historical claims, so the admin disables
  // + clones instead.
  const [code, setCode] = useState(coupon?.code ?? '');
  const [name, setName] = useState(coupon?.name ?? '');
  const [description, setDescription] = useState(coupon?.description ?? '');
  const [discountType, setDiscountType] = useState<CouponUpsertBody['discountType']>(
    coupon?.discountType ?? 'percentage',
  );
  const [discountValue, setDiscountValue] = useState(String(coupon?.discountValue ?? 10));
  const [appliesTo, setAppliesTo] = useState<CouponUpsertBody['appliesTo']>(
    coupon?.appliesTo ?? 'both',
  );
  const [minPurchaseDollars, setMinPurchaseDollars] = useState(
    coupon?.minPurchaseCents ? String(coupon.minPurchaseCents / 100) : '',
  );
  const [maxDiscountDollars, setMaxDiscountDollars] = useState(
    coupon?.maxDiscountCents ? String(coupon.maxDiscountCents / 100) : '',
  );
  const [maxClaimsPerUser, setMaxClaimsPerUser] = useState(
    String(coupon?.maxClaimsPerUser ?? 1),
  );
  const [maxTotalClaims, setMaxTotalClaims] = useState(
    coupon?.maxTotalClaims ? String(coupon.maxTotalClaims) : '',
  );
  // The Los Angeles day of each stored date (lib/couponDates.ts).
  const initialValidFrom = couponDateInput(coupon?.validFrom);
  const initialValidUntil = couponDateInput(coupon?.validUntil);
  const [validFrom, setValidFrom] = useState(initialValidFrom);
  const [validUntil, setValidUntil] = useState(initialValidUntil);
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true);
  const [autoApply, setAutoApply] = useState(coupon?.autoApply ?? true);
  const [submitError, setSubmitError] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      const payload: CouponUpsertBody = {
        code,
        name,
        description: description || undefined,
        discountType,
        discountValue: Number(discountValue),
        appliesTo,
        minPurchaseCents: minPurchaseDollars
          ? Math.round(Number(minPurchaseDollars) * 100) : undefined,
        maxDiscountCents: maxDiscountDollars
          ? Math.round(Number(maxDiscountDollars) * 100) : undefined,
        maxClaimsPerUser: Number(maxClaimsPerUser) || 1,
        maxTotalClaims: maxTotalClaims ? Number(maxTotalClaims) : undefined,
        validFrom: couponDatePayload(validFrom, initialValidFrom, !!coupon),
        validUntil: couponDatePayload(validUntil, initialValidUntil, !!coupon),
        isActive,
        autoApply,
      };
      if (coupon) {
        return updateCouponAdmin(coupon.id, payload);
      }
      return createCouponAdmin(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coupons', 'admin'] });
      onSaved();
    },
    // The server's message, not ky's "Request failed with status code
    // 400": the percentage-ceiling refusal has to read as itself.
    onError: async (err) => {
      setSubmitError(await getDisplayErrorMessage(err, 'Save failed'));
    },
  });

  const isPercentage = discountType === 'percentage';
  const isFixed = discountType === 'fixed_amount';
  const valueLabel =
    isPercentage ? 'Discount %'
    : isFixed ? 'Discount $'
    : 'Bonus points';
  // EVI funds coupons out of its 15% (Warren, 2026-09-26): a percentage
  // coupon above it is refused by the API, so the form stops it first.
  // Fixed-amount coupons have no ceiling here.
  const percentOverCeiling = isPercentage
    && discountValue.trim() !== ''
    && Number(discountValue) > EVI_COUPON_MAX_PERCENT;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={coupon ? `Edit ${coupon.code}` : 'New Coupon'}
      size="lg"
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitError('');
          // The inline error under the value already says why.
          if (percentOverCeiling) return;
          save.mutate();
        }}
      >
        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Code</span>
            <input
              className={styles.input}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              required
              minLength={3}
              maxLength={32}
              disabled={Boolean(coupon)}
              placeholder="WELCOME20"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Welcome bonus"
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.label}>Description <span className={styles.muted}>(optional)</span></span>
          <textarea
            className={styles.input}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Shown on the driver's coupon card."
          />
        </label>

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Discount type</span>
            <select
              className={styles.input}
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as CouponUpsertBody['discountType'])}
              disabled={Boolean(coupon)}
            >
              <option value="percentage">Percentage</option>
              <option value="fixed_amount">Fixed amount</option>
              <option value="bonus_points">Bonus points</option>
            </select>
          </label>
          {/* The note sits outside the <label> so it is the input's
              description, not part of its accessible name. */}
          <div className={styles.field}>
            <label className={styles.field}>
              <span className={styles.label}>{valueLabel}</span>
              <input
                className={`${styles.input} ${percentOverCeiling ? styles.inputInvalid : ''}`}
                type="number"
                min="0"
                max={isPercentage ? EVI_COUPON_MAX_PERCENT : undefined}
                step={isFixed ? '0.01' : '1'}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                required
                aria-invalid={percentOverCeiling}
                aria-describedby={
                  !isPercentage ? undefined
                  : percentOverCeiling ? 'coupon-percent-error' : 'coupon-percent-hint'
                }
              />
            </label>
            {/* Distinct keys: the alert mounts as a new node, so it is
                announced, instead of the hint gaining role="alert". */}
            {isPercentage && (
              percentOverCeiling
                ? <p key="error" id="coupon-percent-error" className={styles.fieldError} role="alert">{EVI_COUPON_PERCENT_MESSAGE}</p>
                : <p key="hint" id="coupon-percent-hint" className={styles.hint}>Up to {EVI_COUPON_MAX_PERCENT}%, EVI&apos;s share.</p>
            )}
          </div>
        </div>

        <label className={styles.field}>
          <span className={styles.label}>Applies to</span>
          <select
            className={styles.input}
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value as CouponUpsertBody['appliesTo'])}
          >
            <option value="both">Charging session or wallet top-up</option>
            <option value="session">Charging session only</option>
            <option value="wallet_topup">Wallet top-up only</option>
          </select>
        </label>

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Min purchase $ <span className={styles.muted}>(optional)</span></span>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="0.01"
              value={minPurchaseDollars}
              onChange={(e) => setMinPurchaseDollars(e.target.value)}
            />
          </label>
          {isPercentage && (
            <label className={styles.field}>
              <span className={styles.label}>Max discount $ <span className={styles.muted}>(optional)</span></span>
              <input
                className={styles.input}
                type="number"
                min="0"
                step="0.01"
                value={maxDiscountDollars}
                onChange={(e) => setMaxDiscountDollars(e.target.value)}
              />
            </label>
          )}
        </div>

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Max claims per user</span>
            <input
              className={styles.input}
              type="number"
              min="1"
              value={maxClaimsPerUser}
              onChange={(e) => setMaxClaimsPerUser(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Max total claims <span className={styles.muted}>(blank = unlimited)</span></span>
            <input
              className={styles.input}
              type="number"
              min="1"
              value={maxTotalClaims}
              onChange={(e) => setMaxTotalClaims(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Valid from <span className={styles.muted}>(optional, start of day, Los Angeles time)</span></span>
            <input
              className={styles.input}
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Valid until <span className={styles.muted}>(optional, end of day, Los Angeles time)</span></span>
            <input
              className={styles.input}
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.toggles}>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label="Active: drivers can claim this coupon"
          />
          <Switch
            checked={autoApply}
            onChange={(e) => setAutoApply(e.target.checked)}
            label="Auto-apply: applies to the next eligible session or top-up automatically"
          />
        </div>

        {submitError && <div className={styles.errorBanner}>{submitError}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : coupon ? 'Save changes' : 'Create coupon'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Formatters ───────────────────────────────────────────────────────
function formatDiscount(c: CouponAdmin): string {
  if (c.discountType === 'percentage') return `${c.discountValue}% off`;
  if (c.discountType === 'fixed_amount') return `$${c.discountValue.toFixed(2)} off`;
  return `+${c.discountValue} pts`;
}

function formatScope(s: CouponAdmin['appliesTo']): string {
  if (s === 'session') return 'Charging';
  if (s === 'wallet_topup') return 'Top-up';
  return 'Both';
}

/** The Los Angeles day a coupon ends on, the same day the form shows. */
function formatDate(iso: string): string {
  try { return formatLosAngelesDate(iso, undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}
